import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Table from 'react-bootstrap/Table';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';

import eventsActionCreators from '_store/_actions/events';

function SendWhatsAppNotificationModal({ 
  show, 
  onHide, 
  eventId, 
  config, 
  registrations,
  onSendWhatsAppNotification,
  event
}) {
  const [selectedRegistrations, setSelectedRegistrations] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [previewMessage, setPreviewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (registrations && registrations.length > 0) {
      // Reset selections when modal opens
      setSelectedRegistrations([]);
      setSelectAll(false);
    }
  }, [registrations, show]);

  useEffect(() => {
    if (config && config.template && registrations && registrations.length > 0) {
      // Generate a preview with the first registration
      const firstReg = registrations[0];
      const preview = generateMessagePreview(config.template, firstReg);
      setPreviewMessage(preview);
    }
  }, [config, registrations]);

  const generateMessagePreview = (template, registration) => {
    if (!template || !registration) return '';

    let message = template;
    // Replace placeholders with actual values
    message = message.replace(/{name}/g, registration.name || '');
    message = message.replace(/{email}/g, registration.email || '');
    message = message.replace(/{phone}/g, registration.phone || '');
    message = message.replace(/{event_name}/g, event?.name || '');
    message = message.replace(/{event_date}/g, event?.start_date ? 
      new Date(event.start_date).toLocaleDateString() : '');
    message = message.replace(/{event_location}/g, event?.address || '');

    // Replace any custom field placeholders
    if (registration.custom_fields) {
      Object.keys(registration.custom_fields).forEach(field => {
        const value = registration.custom_fields[field];
        message = message.replace(new RegExp(`{${field}}`, 'g'), value || '');
      });
    }

    return message;
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    
    if (checked) {
      // Select all registrations
      setSelectedRegistrations(registrations.map(reg => reg._id));
    } else {
      // Deselect all
      setSelectedRegistrations([]);
    }
  };

  const handleSelectRegistration = (e, registrationId) => {
    const checked = e.target.checked;
    
    if (checked) {
      // Add to selected
      setSelectedRegistrations([...selectedRegistrations, registrationId]);
    } else {
      // Remove from selected
      setSelectedRegistrations(selectedRegistrations.filter(id => id !== registrationId));
    }
  };

  const handleSendNotifications = async () => {
    if (selectedRegistrations.length === 0) {
      toastr.warning('Warning', 'Please select at least one recipient');
      return;
    }

    setIsSending(true);

    try {
      await onSendWhatsAppNotification(
        eventId, 
        config._id,
        selectedRegistrations
      );
      
      toastr.success('Success', `WhatsApp notifications sent to ${selectedRegistrations.length} recipient(s)`);
      onHide();
    } catch (error) {
      toastr.error('Error', error.message || 'Failed to send WhatsApp notifications');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Send WhatsApp Notification</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {config && (
          <>
            <h5>Template: {config.name}</h5>
            <Alert variant="info">
              <strong>Message Preview:</strong>
              <p className="mt-2 mb-0" style={{ whiteSpace: 'pre-line' }}>{previewMessage}</p>
            </Alert>

            <div className="mt-4 mb-3">
              <h5>Select Recipients</h5>
              <div className="mb-2">
                <Form.Check 
                  type="checkbox"
                  id="select-all"
                  label={`Select All (${registrations.length})`}
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
              </div>
              
              {registrations.length > 0 ? (
                <Table hover responsive>
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>Select</th>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.map((registration) => (
                      <tr key={registration._id}>
                        <td>
                          <Form.Check 
                            type="checkbox"
                            checked={selectedRegistrations.includes(registration._id)}
                            onChange={(e) => handleSelectRegistration(e, registration._id)}
                          />
                        </td>
                        <td>{registration.name}</td>
                        <td>{registration.phone || 'No phone'}</td>
                        <td>
                          {registration.status === 'waitlist' ? 'Waitlist' : 'Confirmed'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="warning">
                  No registrations available to send notifications to.
                </Alert>
              )}
              
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  <strong>{selectedRegistrations.length}</strong> recipient(s) selected
                </div>
                <Button
                  variant="primary"
                  disabled={selectedRegistrations.length === 0 || isSending}
                  onClick={handleSendNotifications}
                >
                  {isSending ? 'Sending...' : 'Send WhatsApp Notifications'}
                </Button>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSending}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

SendWhatsAppNotificationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  eventId: PropTypes.string.isRequired,
  config: PropTypes.object,
  registrations: PropTypes.array.isRequired,
  onSendWhatsAppNotification: PropTypes.func.isRequired,
  event: PropTypes.object
};

const mapStateToProps = (state) => ({
  event: state.events.event
});

const mapDispatchToProps = {
  onSendWhatsAppNotification: eventsActionCreators.sendWhatsAppNotification
};

export default connect(mapStateToProps, mapDispatchToProps)(SendWhatsAppNotificationModal);
